import React, { useEffect, useRef, useState } from 'react';
import './MyUpsetTrackerDetail.css';

const imageBase = '/images/my upset tracker asset';

const content = {
  en: {
    back: '← My Work',
    heroKicker: 'iOS App · Mood Journal',
    heroTitle: 'My Upset Tracker (Vessel)',
    heroSub: 'A vessel that holds whatever you are feeling.',
    heroBody1:
      'This app tries to do something quiet — when you are at your lowest, or your happiest, it helps you mark the moment. You do not have to explain it to anyone, and you do not have to feel better right away. All you need is to pause once a day and tell yourself, on a scale from 1 to 10, "this is where I am right now."',
    heroBody2:
      'Over time, those small entries become a map of your inner weather — not so you can compare your "good" and "bad" days, but so you can finally see and listen to your own voice.',
    whyKicker: 'Why this app exists',
    whyTitle: 'Some days, even saying "I\u2019m not okay" is too hard.',
    whyP1:
      'I made this app because I have been through that nameless low. Every day felt heavy, and yet it felt like there was no one I could lean on. I wanted a safe space where I could lay my feelings down — without being judged, without needing to be understood.',
    whyP2:
      'So this app has no checklist of "goals", no fake positivity, no streak counter telling you that you missed a day. It only wants to do one thing — sit beside you, on the days you are willing to sit down.',
    showcaseKicker: 'Inside the App',
    showcaseTitle: 'Four small corners, four different vessels for emotion.',

    quote:
      '\u201cYour mental attitude determines what you make of it, either a stepping stone or a stumbling block.\u201d',

    footerBack: '← Back to My Work',
    footerNote: 'Vessel · 2026',
    slides: [
      {
        eyebrow: 'Today',
        title: 'Marking how you feel right now',
        body: 'Each day is split into two parts — morning and afternoon. Tap a number from 1 to 10 to leave the most honest read of how you feel, or write a short note to put down whatever you couldn\u2019t say out loud today. Quietly, this space holds it for you.',
      },
      {
        eyebrow: 'Calendar',
        title: 'Looking back at every day',
        body: 'The mood calendar paints each day with a color: very good, okay, slightly low, low, and days with bigger swings. Looking back, you can see which days carried more weight, and which days felt lighter — and gently understand the season of yourself you were living through.',
      },
      {
        eyebrow: 'Evaluation',
        title: 'Seeing the shape of your emotions',
        body: 'The Daily Mood Chart connects morning and afternoon scores into a single line, marking days that have notes with little icons. Feelings are not always linear — sometimes happy, sometimes low, sometimes calm. Here you can see the curve of your own emotions, and how it slowly shifts.',
      },
      {
        eyebrow: 'Story',
        title: 'Heart Trail — a corner just for you',
        body: 'Heart Trail is a private space that belongs only to you. You can write a paragraph, sketch something, record a voice note, or even capture a short video. Nothing has to be sent, understood, or replied to — it only gives that emotion somewhere to rest.',
      },
    ],
  },
  zh: {
    back: '← My Work',
    heroKicker: 'iOS App · 情緒紀錄',
    heroTitle: 'My Upset Tracker (Vessel)',
    heroSub: '一個承托情緒的容器。',
    heroBody1:
      '這個 app 想做的事情很簡單 — 在情緒最沉的時候，或者是你開心的時候，幫你把那一刻記下來。不需要解釋給誰聽，也不需要立刻變得更好。只要每天願意停下來一次，用 1 到 10 的數字告訴自己：「我現在的狀態，是這樣。」',
    heroBody2:
      '時間久了，這些紀錄會變成一張屬於你的情緒地圖。不是為了比較哪天比較好，而是為了讓自己看見，讓自己聽見自己的聲音。',
    whyKicker: 'Why this app exists',
    whyTitle: '有些日子，連說「我不太好」都很難。',
    whyP1:
      '做這個 App 是因為我自己也曾經有過那種無法形容的低潮。每天都覺得難過，但又感覺沒有人能夠陪伴。我想要一個安全的地方，可以放下自己的心情，不需要被評價，也不需要被理解。',
    whyP2:
      '所以這個 app 沒有打勾的「目標」，沒有正能量的提示，也沒有比賽連續打卡幾天或者通知你今天還沒有打卡。它只想做一件事 — 在你願意的那一天，陪你坐一下。',
    showcaseKicker: 'Inside the App',
    showcaseTitle: '四個小角落，不同的情緒容器。',

    quote:
      '「你的心態決定了你怎麼看待它 —\n是一塊往前走的踏腳石，還是一塊讓你絆倒的石頭。」',

    footerBack: '← Back to My Work',
    footerNote: 'Vessel · 2026',
    slides: [
      {
        eyebrow: 'Today',
        title: '紀錄此刻的心情',
        body: '每一天分成兩個段落 — 上午與下午。點擊 1 到 10 的能量條，留下這個時段最真實的感受。或者寫一段短短的心情筆記，把今天說不出口的事情，安靜地交給這個空間。',
      },
      {
        eyebrow: 'Calendar',
        title: '回顧每一天的情緒',
        body: '情緒日曆把每一天的心情上色：很好、還好、偏低、低落，還有波幅較大的日子。回頭看的時候，可以看到自己在哪些日子比較沉，哪些日子比較開心。',
      },
      {
        eyebrow: 'Evaluation',
        title: '看見情緒的軌跡',
        body: 'Daily Mood Chart 把上午與下午的分數連成一條折線，用顏色和圖示標記有筆記的日子。情緒可能或不是線性的，或者開心，或者低落，或者平靜。你可以看到自己的情緒軌跡及筆記，也可以看到自己的情緒如何變化。',
      },
      {
        eyebrow: 'Story',
        title: '心跡 — 留給自己的角落',
        body: '心跡是一個只屬於自己的私人空間。可以寫下一段文字、塗鴉、錄一段聲音，甚至錄一段影像。不用發出去，不用被理解，也不用被回應。只是讓那個情緒，有地方安放。',
      },
    ],
  },
};

const slideImages = [
  `${imageBase}/IMG_7799.jpg`,
  `${imageBase}/IMG_7800.PNG`,
  `${imageBase}/IMG_7801.PNG`,
  `${imageBase}/IMG_7802.PNG`,
];

function RevealBlock({ children, className = '', delay = 0 }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('visible');
          observer.unobserve(el);
        }
      },
      { threshold: 0.16 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`mut-reveal ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function MyUpsetTrackerDetail() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [lang, setLang] = useState('en');
  const t = content[lang];
  const slide = { ...t.slides[activeSlide], image: slideImages[activeSlide] };
  const total = t.slides.length;

  const goPrev = () => setActiveSlide((c) => (c === 0 ? total - 1 : c - 1));
  const goNext = () => setActiveSlide((c) => (c === total - 1 ? 0 : c + 1));
  const toggleLang = () => setLang((l) => (l === 'en' ? 'zh' : 'en'));

  return (
    <div className="mut-page" lang={lang}>
      <div className="mut-rain" aria-hidden="true">
        {Array.from({ length: 40 }).map((_, i) => (
          <span
            key={i}
            style={{
              left: `${(i * 2.7) % 100}%`,
              animationDelay: `${(i * 0.31) % 6}s`,
              animationDuration: `${5 + ((i * 0.7) % 4)}s`,
            }}
          />
        ))}
      </div>

      <button
        className="mut-lang"
        onClick={toggleLang}
        aria-label="Toggle language"
        title={lang === 'en' ? '切換到中文' : 'Switch to English'}
      >
        <svg
          className="mut-lang-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18" />
          <path d="M12 3a14 14 0 0 1 0 18" />
          <path d="M12 3a14 14 0 0 0 0 18" />
        </svg>
        <span className="mut-lang-label">{lang === 'en' ? '中文' : 'EN'}</span>
      </button>

      {/* Hero */}
      <section className="mut-hero">
        <div className="mut-hero-haze mut-hero-haze-a" />
        <div className="mut-hero-haze mut-hero-haze-b" />

        <RevealBlock className="mut-hero-grid">
          <div className="mut-hero-copy">
            <span className="mut-kicker">{t.heroKicker}</span>
            <h1 className="mut-hero-title">{t.heroTitle}</h1>
            <p className="mut-hero-sub">{t.heroSub}</p>
            <p className="mut-hero-body">{t.heroBody1}</p>
            <p className="mut-hero-body">{t.heroBody2}</p>
            <div className="mut-tech-row">
              {['React Native', 'Expo', 'Local Storage', 'Charts'].map(
                (tech) => (
                  <span key={tech}>{tech}</span>
                )
              )}
            </div>
          </div>

          <div className="mut-icon-stage">
            <div className="mut-icon-orbit" />
            <img
              src={`${imageBase}/ChatGPT Image Apr 29, 2026 at 01_43_29 PM.webp`}
              alt="My Upset Tracker app icon"
              className="mut-app-icon"
            />
          </div>
        </RevealBlock>
      </section>

      {/* Why */}
      <section className="mut-why">
        <RevealBlock className="mut-why-card">
          <span className="mut-kicker">{t.whyKicker}</span>
          <h2>{t.whyTitle}</h2>
          <p>{t.whyP1}</p>
          <p>{t.whyP2}</p>
        </RevealBlock>
      </section>

      {/* Showcase */}
      <section className="mut-showcase">
        <RevealBlock className="mut-showcase-header">
          <span className="mut-kicker">{t.showcaseKicker}</span>
          <h2>{t.showcaseTitle}</h2>
        </RevealBlock>

        <RevealBlock className="mut-showcase-stage">
          <div className="mut-phone-panel">
            <div className="mut-phone-frame">
              <img src={slide.image} alt={slide.title} />
            </div>
          </div>

          <div className="mut-slide-copy">
            <span className="mut-slide-count">
              {String(activeSlide + 1).padStart(2, '0')} /{' '}
              {String(total).padStart(2, '0')}
            </span>
            <span className="mut-slide-eyebrow">{slide.eyebrow}</span>
            <h3>{slide.title}</h3>
            <p>{slide.body}</p>

            <div className="mut-controls">
              <button onClick={goPrev} aria-label="Previous slide">
                ←
              </button>
              <div className="mut-dots">
                {t.slides.map((item, idx) => (
                  <button
                    key={item.title}
                    className={idx === activeSlide ? 'active' : ''}
                    onClick={() => setActiveSlide(idx)}
                    aria-label={`Go to ${item.title}`}
                  />
                ))}
              </div>
              <button onClick={goNext} aria-label="Next slide">
                →
              </button>
            </div>
          </div>
        </RevealBlock>
      </section>

      {/* Quote */}
      <section className="mut-quote">
        <RevealBlock className="mut-quote-block">
          <span className="mut-kicker">{t.quoteKicker}</span>
          <p className="mut-quote-text">{t.quote}</p>
          <span className="mut-quote-author">{t.quoteAuthor}</span>
        </RevealBlock>
      </section>

      {/* Footer */}
      <footer className="mut-footer">
        <p>{t.footerNote}</p>
      </footer>
    </div>
  );
}
